import "./SideWindow.scss";

import { useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useLocalStorage } from "react-use";

import { useCanSeeLabStatus } from "shared/hooks/usePermissions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import AnnotationsList from "./AnnotationsList";
import CaseLog from "./CaseLog";
import LabWorkStatus from "./LabWorkStatus";
import LinkButton from "../LinkButton";

interface SideWindowProps {
  slideDetail?: SlideDetailState;
  caseDetail?: CaseDetailState;
  features: FeatureFlags;
  hasCaseLog?: boolean;
  hasSlideAnnotations?: boolean;
  amended: boolean;
  setSideWindowOpen: Function;
}

// TODO: Make these tabs
function SideWindow(props: SideWindowProps) {
  const CASE_LOG_TAB = "Case Log";
  const SLIDE_ANNOTATIONS_TAB = "Slide Annotations";
  const WORK_STATUS_TAB = "Lab Status";
  const canSeeLabStatus = useCanSeeLabStatus();

  const [activeTab, setActiveTab] = useLocalStorage<string>(
    "SideWindow.activeTab", // We need a naming convention for these
    SLIDE_ANNOTATIONS_TAB
  );

  // Motive here: Scroll down & show off spinner when new annotations get added
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      activeTab === SLIDE_ANNOTATIONS_TAB &&
      props.slideDetail &&
      props.slideDetail.pendingAnnotationsBuffer.length > 0
    ) {
      scrollableRef.current?.scrollTo(0, scrollableRef.current?.scrollHeight);
    }
  }, [
    props.hasSlideAnnotations,
    activeTab,
    props.slideDetail,
    props.slideDetail?.pendingAnnotationsBuffer,
  ]);

  useHotkeys(
    "Escape",
    () => props.setSideWindowOpen(false),
    { enabled: props.features.kwonKeys, scopes: ["viewerHotkeys"] },
    []
  );

  return (
    <div
      className="SideWindow"
      onClick={(event) => {
        // Parent DIV is class InvisibleButton with an onClick that will close this component;
        // this is to prevent clicks inside this component from getting to it.
        event.stopPropagation();
      }}
    >
      <header>
        <span className="wide_spacer">&nbsp;</span>
        {activeTab === CASE_LOG_TAB ? (
          <span className="active_tab">{CASE_LOG_TAB}</span>
        ) : (
          <LinkButton
            className="inactive_tab"
            onClick={() => {
              setActiveTab(CASE_LOG_TAB);
            }}
          >
            {CASE_LOG_TAB}
          </LinkButton>
        )}
        <span className="spacer">&nbsp;</span>
        {!!props.slideDetail &&
          (activeTab === SLIDE_ANNOTATIONS_TAB ? (
            <span className="active_tab">Slide Annotations</span>
          ) : (
            <LinkButton
              className="inactive_tab"
              onClick={() => {
                setActiveTab(SLIDE_ANNOTATIONS_TAB);
              }}
            >
              {SLIDE_ANNOTATIONS_TAB}
            </LinkButton>
          ))}
        {canSeeLabStatus && (
          <>
            <span className="spacer">&nbsp;</span>
            {activeTab === WORK_STATUS_TAB ? (
              <span className="active_tab">{WORK_STATUS_TAB}</span>
            ) : (
              <LinkButton
                className="inactive_tab"
                onClick={() => {
                  setActiveTab(WORK_STATUS_TAB);
                }}
              >
                {WORK_STATUS_TAB}
              </LinkButton>
            )}
          </>
        )}
        <span className="wide_spacer">&nbsp;</span>
      </header>
      <div ref={scrollableRef} className="vertically-scrollable">
        {props.caseDetail && activeTab === CASE_LOG_TAB ? (
          props.hasCaseLog === true ? (
            <CaseLog amended={props.amended} caseDetail={props.caseDetail} />
          ) : (
            <div className="sorry">CASE LOG EMPTY</div>
          )
        ) : (
          <></>
        )}
        {props.slideDetail && activeTab === SLIDE_ANNOTATIONS_TAB ? (
          props.hasSlideAnnotations === true ? (
            <AnnotationsList
              slideDetail={props.slideDetail}
              features={props.features}
            />
          ) : (
            <div className="sorry">NO SLIDE ANNOTATIONS</div>
          )
        ) : props.caseDetail &&
          activeTab === WORK_STATUS_TAB &&
          canSeeLabStatus ? (
          <LabWorkStatus />
        ) : null}
      </div>
    </div>
  );
}

export default SideWindow;
